import { useEffect, useState } from 'react';

// Sentinel stored in localStorage when the user has explicitly chosen the
// built-in default template. Distinguishes "returning user, explicit
// default pick" from "first-ever visit, key absent". Exported so callers
// can key it out of their own migration paths if needed.
export const DEFAULT_TEMPLATE_SENTINEL = '__default__';

interface TemplateLike {
  templateId: string;
}

/**
 * Persistent template picker state, shared between the bundle-index and
 * chronology download modals.
 *
 * - `templates` is the live list from the backend; when it loads we
 *   reconcile the cached id — if the cached template has been deleted
 *   we clear it and fall back to the built-in default (null).
 * - Every user change is persisted; null is stored as the sentinel so a
 *   returning user's explicit "Default template" pick isn't confused
 *   with a first-ever visit.
 * - SSR-safe: reads/writes localStorage only when `window` exists.
 */
export const useLocalStorageTemplateId = (
  storageKey: string,
  templates: readonly TemplateLike[],
): [string | null, (id: string | null) => void] => {
  const [id, setId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const cached = window.localStorage.getItem(storageKey);
    if (!cached || cached === DEFAULT_TEMPLATE_SENTINEL) return null;
    return cached;
  });

  // Reconcile once the list loads: if the cached id no longer resolves
  // to a real template, drop it. Depending on `templates` directly is
  // fine — the effect early-returns when the list is empty (still loading)
  // or when we've already got null.
  useEffect(() => {
    if (templates.length === 0) return;
    if (id === null) return;
    const stillExists = templates.some((t) => t.templateId === id);
    if (!stillExists) {
      setId(null);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(storageKey);
      }
    }
    // Only reconcile when the list itself changes; `id` is intentionally
    // omitted so a user-triggered setId(null) doesn't re-fire this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, storageKey]);

  // Persist on every change.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, id ?? DEFAULT_TEMPLATE_SENTINEL);
  }, [id, storageKey]);

  return [id, setId];
};
